import { memoryToClaude } from './memory-to-claude.js';
import { memoryToAgents } from './memory-to-agents.js';
import { claudeToMemory } from './claude-to-memory.js';
import { agentsToMemory } from './agents-to-memory.js';
import { TargetFormat, SourceFormat, CompileConfig } from '../types/index.js';

/**
 * Convert from MEMORY.md to target format
 */
export async function convertFromMemory(
  sourcePath: string,
  targetPath: string,
  targetFormat: TargetFormat,
  config: CompileConfig
): Promise<{ warnings: string[]; errors: string[] }> {
  const normalizedFormat = normalizeTargetFormat(targetFormat);

  switch (normalizedFormat) {
    case 'CLAUDE.md':
      await memoryToClaude(sourcePath, targetPath);
      return { warnings: [], errors: [] };

    case 'AGENTS.md':
      return await memoryToAgents(sourcePath, targetPath, config);

    default:
      throw new Error(`Unsupported target format: ${targetFormat}`);
  }
}

/**
 * Convert from source format to MEMORY.md
 */
export async function convertToMemory(
  sourcePath: string,
  targetPath: string,
  sourceFormat: SourceFormat
): Promise<void> {
  switch (sourceFormat) {
    case 'CLAUDE.md':
      await claudeToMemory(sourcePath, targetPath);
      break;

    case 'AGENTS.md':
      await agentsToMemory(sourcePath, targetPath);
      break;

    case 'MEMORY.md':
      throw new Error('Source is already MEMORY.md');

    default:
      throw new Error(`Unsupported source format: ${sourceFormat}`);
  }
}

/**
 * Normalize target format aliases to actual filenames
 */
export function normalizeTargetFormat(format: TargetFormat): 'CLAUDE.md' | 'AGENTS.md' {
  const normalized = format.toLowerCase();

  if (normalized === 'claude' || normalized === 'claude.md') {
    return 'CLAUDE.md';
  }

  if (
    normalized === 'agents' ||
    normalized === 'agents.md' ||
    normalized === 'codex' ||
    normalized === 'gemini'
  ) {
    return 'AGENTS.md';
  }

  throw new Error(`Unknown format: ${format}`);
}

/**
 * Get output filename for a target format
 */
export function getOutputFilename(format: TargetFormat): string {
  return normalizeTargetFormat(format);
}

/**
 * Detect format from filename
 */
export function detectFormat(filename: string): SourceFormat | null {
  const normalized = filename.toUpperCase();

  if (normalized === 'MEMORY.MD') return 'MEMORY.md';
  if (normalized === 'CLAUDE.MD') return 'CLAUDE.md';
  if (normalized === 'AGENTS.MD') return 'AGENTS.md';

  return null;
}
